import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AccountResult {
  email: string;
  displayName: string;
  userId?: string;
  error?: string;
}

const CreateUsaTeamAccounts = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<AccountResult[]>([]);
  const { toast } = useToast();

  const handleCreateAccounts = async () => {
    setLoading(true);
    setResults([]);

    try {
      console.log('Calling create-usa-team-accounts function...');
      
      const { data, error } = await supabase.functions.invoke('create-usa-team-accounts');

      if (error) {
        console.error('Function error:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to create accounts",
          variant: "destructive"
        });
        return;
      }

      console.log('Function response:', data);

      const results: AccountResult[] = [
        ...data.results.created.map((email: string) => ({
          email,
          displayName: email.split('@')[0].split('.').map((s: string) => 
            s.charAt(0).toUpperCase() + s.slice(1)
          ).join(' '),
          userId: 'created'
        })),
        ...data.results.errors.map((err: { email: string; error: string }) => ({
          email: err.email,
          displayName: err.email.split('@')[0].split('.').map((s: string) => 
            s.charAt(0).toUpperCase() + s.slice(1)
          ).join(' '),
          error: err.error
        }))
      ];

      setResults(results);

      if (data.results.created.length > 0) {
        toast({
          title: "Success",
          description: `Created ${data.results.created.length} account(s) successfully with all roles and memberships configured`
        });
      }

      if (data.results.errors.length > 0) {
        toast({
          title: "Partial Success",
          description: `${data.results.errors.length} account(s) failed to create`,
          variant: "destructive"
        });
      }

    } catch (error: any) {
      console.error('Unexpected error:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted p-8">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Create USA Channel Team Accounts</CardTitle>
            <CardDescription>
              Create full auth accounts for Heather Edwards and Charles Hardy with password123. All roles, channel memberships, and profile mappings will be configured automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold mb-2">Accounts to be created:</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Heather Edwards (heather.edwards@gsa.gov) - Password: password123</li>
                  <li>• Charles Hardy (charles.hardy@dot.gov) - Password: password123</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  This will also assign ispai_team roles to all three team members (including Julia) and configure channel memberships.
                </p>
              </div>

              <Button 
                onClick={handleCreateAccounts} 
                disabled={loading || results.length > 0}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Accounts...
                  </>
                ) : (
                  "Create Both Accounts"
                )}
              </Button>

              {results.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold">Results:</h3>
                  {results.map((result, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg flex items-start gap-3 ${
                        result.error ? 'bg-red-50' : 'bg-green-50'
                      }`}
                    >
                      {result.error ? (
                        <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">{result.displayName}</p>
                        <p className="text-sm text-muted-foreground">{result.email}</p>
                        {result.error && (
                          <p className="text-sm text-red-600 mt-1">{result.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateUsaTeamAccounts;
